
import Vue from "vue";
import {
  getHygienistBlocksFavourites,
  getLocationBlocksFavourites,
  addBlocked,
  removeBlocked,
  addFavourite,
  removeFavourite
} from "@/api";
import EntityIdLink from "@/components/shared/EntityIdLink";

type Kind = "block" | "favourite";

export default Vue.extend({
  name: "BlocksFavourites",
  components: { EntityIdLink },
  props: {
    // 'talent' → blocked/favorited OFFICES; 'location' → blocked/favorited TALENT
    mode: { type: String as () => "talent" | "location", required: true },
    entityId: { type: Number, required: true }
  },
  data() {
    return {
      loading: false,
      blocked: [] as any[],
      favourites: [] as any[],
      removeDialog: false,
      addDialog: false,
      pendingRow: null as any,
      pendingKind: "block" as Kind,
      removingId: "" as string,
      addEntityId: "" as string,
      adding: false
    };
  },
  computed: {
    blockTable(): string {
      return this.mode === "talent" ? "BlockedDentist" : "BlockedHygienist";
    },
    favouriteTable(): string {
      return this.mode === "talent" ? "FavouriteDentist" : "FavouriteHygienist";
    }
  },
  watch: {
    entityId: {
      immediate: true,
      handler() {
        this.fetch();
      }
    }
  },
  methods: {
    rowLabel(row: any): string {
      if (this.mode === "talent") {
        const place = [row.city, row.province].filter(Boolean).join(", ");
        return `${row.practiceName || "Unknown office"}${
          place ? ` — ${place}` : ""
        }`;
      }
      const name = [row.firstName, row.lastName].filter(Boolean).join(" ");
      return name || "Unknown talent";
    },
    snackbar(text: string, color: string) {
      this.$store.commit("callSnackbar", { visible: true, text, color });
    },
    async fetch() {
      if (!this.entityId) return;
      this.loading = true;
      try {
        if (this.mode === "talent") {
          const data = await getHygienistBlocksFavourites(this.entityId);
          this.blocked = data.blockedOffices || [];
          this.favourites = data.favouriteOffices || [];
        } else {
          const data = await getLocationBlocksFavourites(this.entityId);
          this.blocked = data.blockedTalent || [];
          this.favourites = data.favouriteTalent || [];
        }
      } catch (error) {
        console.error("Failed to load blocks & favourites", error);
        this.blocked = [];
        this.favourites = [];
      } finally {
        this.loading = false;
      }
    },
    confirmRemove(row: any, kind: Kind) {
      this.pendingRow = row;
      this.pendingKind = kind;
      this.removeDialog = true;
    },
    async doRemove() {
      if (!this.pendingRow) return;
      const kind = this.pendingKind;
      const row = this.pendingRow;
      this.removeDialog = false;
      this.removingId = `${kind}-${row.id}`;
      try {
        if (kind === "block") {
          await removeBlocked(this.blockTable, row.id);
          this.blocked = this.blocked.filter(r => r.id !== row.id);
        } else {
          await removeFavourite(this.favouriteTable, row.id);
          this.favourites = this.favourites.filter(r => r.id !== row.id);
        }
        this.snackbar("Removed.", "#13CA82");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Remove failed";
        this.snackbar(`Could not remove: ${message}`, "#FB7672");
        this.fetch();
      } finally {
        this.removingId = "";
        this.pendingRow = null;
      }
    },
    openAdd(kind: Kind) {
      this.pendingKind = kind;
      this.addEntityId = "";
      this.addDialog = true;
    },
    async doAdd() {
      const otherId = Number(this.addEntityId);
      if (!otherId || otherId <= 0) return;
      this.adding = true;
      try {
        const data =
          this.mode === "talent"
            ? { hygienistId: this.entityId, locationId: otherId }
            : { hygienistId: otherId, locationId: this.entityId };
        if (this.pendingKind === "block") {
          await addBlocked(data, this.blockTable);
        } else {
          await addFavourite(data, this.favouriteTable);
        }
        this.addDialog = false;
        this.snackbar("Added.", "#13CA82");
        await this.fetch();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Add failed";
        this.snackbar(`Could not add: ${message}`, "#FB7672");
      } finally {
        this.adding = false;
      }
    }
  }
});
